import React, { useState, useEffect, Fragment, useRef } from "react";
import { Helmet } from "react-helmet";
import { Link, useHistory, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import PropTypes from "prop-types";

import {
	logInUser, logOutUser, clearApiKeys,
	setLogInTimeoutStartTime, clearLogInTimeout, clearLoginAttemptCount,
	setIP, setIPLocked, setIPCreatedAt,
	setProfileSubNav
} from "redux/actions/actionTypes";
import { LOG_IN_TIMEOUT } from "redux/actions/accountConstants";
import navigationSelector from "redux/selectors/navigationSelector";
import store from "redux/store";

import { modalLawSchema, modalSchema } from "templates/schemas/navBarSchemas";

import Username from "./components/Username";
import Alert from "templates/Alert";
import Modal from "templates/Modal";
import Spinner from "templates/Spinner";
import PrivacyPolicyModal from "templates/customModals/PrivacyPolicyModal";
import TermsNConditionsModal from "templates/customModals/TermsNConditionsModal";
import Notification from "templates/Notification";

import { loginAPI, autoLoginAPI, signUpAPI, logOutAPI, usernameAvailabilityAPI } from "apis/controllers/person/AccountsController";
import { unblockIpAPI, blockIpPostAPI, blockIpGetAPI } from "apis/controllers/IPsController";
import { getReleaseAPI } from "apis/controllers/customer/AccessController";
import { listAllPromoCodesAPI } from "apis/controllers/customer/PromotionsController";

import { initializeExplicitDictionaryTree, checkHasExplicitWords } from "Utilities/Validators/ContentValidator.js";
import { useChangeDomByWindowWidth } from "Utilities/CustomHooks";
import messageMap from "Utilities/MessageMaps";
import { getAlt, importShinyNeuron } from 'Utilities/ShinyNeuronImages';
import { generateAllKnowledgeNetworks } from "diagrams/utilities/NetworkGenerator";
import { ACCOUNT, TUTORING, GROUPS, FINANCIALS } from "templates/utilities/PageSubMenu";

import accountAsset from "assets/icons/navigation/account.svg";
import uploadAsset from "assets/icons/navigation/upload.svg";
import editVideosAsset from "assets/icons/navigation/edit-video.svg";
import profileSettingsAsset from "assets/icons/navigation/profile-settings.svg";
import shinyNeuronsAsset from "assets/icons/navigation/shiny-neuron.svg";
import groupsAsset from "assets/icons/navigation/groups.svg";
import logoutAsset from "assets/icons/common/leave.svg";
// used for topNav and sideNav links
import learnAsset from "assets/icons/navigation/learn.svg";
import learnBlackAsset from "assets/icons/navigation/learn-black.svg";
import practiceAsset from "assets/icons/navigation/practice.svg";
import practiceBlackAsset from "assets/icons/navigation/practice-black.svg";
import textbookAsset from "assets/icons/navigation/textbook.svg";
import textbookBlackAsset from "assets/icons/navigation/textbook-black.svg";
import networksAsset from "assets/icons/navigation/networks.svg";
import networksBlackAsset from "assets/icons/navigation/networks-black.svg";
import aboutUsAsset from "assets/icons/navigation/about-us.svg";
// sub page
import accountSubOptionAsset from "assets/icons/navigation/pageSubMenus/account/user.svg";
import groupsSubOptionAsset from "assets/icons/navigation/pageSubMenus/account/groups.svg";
import financialsSubOptionAsset from "assets/icons/navigation/pageSubMenus/account/financials.svg";

import hamburgerAsset from "assets/icons/common/hamburger-menu.svg";
import xWhiteAsset from "assets/icons/common/x-white.svg";
import exerLogoAsset from "assets/images/home/exer_logo.svg";
import exerLogoBannerAsset from "assets/icons/common/exer_logo_name.svg";
import searchAsset from "assets/icons/home/search.svg";
import checkAsset from "assets/icons/home/check.svg";
import exAsset from "assets/icons/home/ex.svg";
import notificationAsset from "assets/icons/common/notification.svg";


/**
 * @description Serves as the navbar on every page
 * @param {String} ownerId?  // redux prop current user's ownerId
 * @param {String} username? // user's username, provided via model submitHandler logIn()
 * @param {Function} logInUser // redux actions: sets ownerId
 * @param {Function} logOutUser // redux actions: removes ownerId from state
 */
function NavBar(props) {
	const [spinner, setSpinner] = useState(""),
		[domModal, setDomModal] = useState(""),
		[domModal1, setDomModal1] = useState(""),
		[domModal2, setDomModal2] = useState(""),
		[alert, setAlert] = useState(null),
		[ownerId, setOwnerId] = useState(props.ownerId),
		[accountDisplay, setAccountDisplay] = useState(""),
		[notificationContents, setNotificationContents] = useState(),
		[notificationDot, setNotificationDot] = useState(""),
		[notificationDisplay, setNotificationDisplay] = useState(""),
		[initializedOnce, setInitializedOnce] = useState(null),
		// json-ld
		[structuredLogoJSON, setStructuredLogoJSON] = useState();

	// value ref
	const userSessionRef = useRef({}),
		sideNavRef = useRef(),
		sideNavContainerRef = useRef(),
		notificationsContainerRef = useRef(),
		videoListClassNameRef = useRef(),
		practiceClassNameRef = useRef(),
		topicsInfoListClassNameRef = useRef(),
		questsClassNameRef = useRef();

	// DOM ref
	const notificationRef = useRef(),
		hamburgerRef = useRef(),
		xIconRef = useRef(),
		// username
		usernameDropdownRef = useRef(),
		usernameChevronRef = useRef(),
		// profile
		profileDropdownRef = useRef(),
		profileChevronRef = useRef();

	const history = useHistory();
	const location = useLocation();
	const neuronImage = importShinyNeuron(1);
	const neuronImageAltText = getAlt(1);
	const paddedNeuronBalance = Math.min(props.shinyNeurons, 9999).toString().padStart(4, '0');
	let navBarClass,
		loginAttemptCount = 0;

	let topNav = useChangeDomByWindowWidth(1100,
		<div className="action-container">
			{
				props.ownerId ?
					<Fragment>
						<div className="button-group-container">
							{/* TODO: Only uncomment once we create associated features for creating, updating, deleting, and viewing classes */}
							{/* <div className="class-list">
							<Link to="/class-list">
								Classes
							</Link>
						</div> */}
							<div className="class-list" tabIndex={0}
								onClick={indicateCurrentPage} onKeyPress={indicateCurrentPage}>
								<Link to="/video-list">
									<img src={learnAsset} alt={messageMap("navigation.videoList", "image")}
										className={videoListClassNameRef.current}></img>
								</Link>
							</div>
							<div className="class-list" tabIndex={0}
								onClick={indicateCurrentPage} onKeyPress={indicateCurrentPage}>
								<Link to="/practice-category">
									<img src={practiceAsset} alt={messageMap("navigation.practice", "image")}
										className={practiceClassNameRef.current}></img>
								</Link>
							</div>
							<div className="class-list" tabIndex={0}
								onClick={indicateCurrentPage} onKeyPress={indicateCurrentPage}>
								<Link to="/topics-info-list">
									<img src={textbookAsset} alt={messageMap("navigation.topicsInfoList", "image")}
										className={topicsInfoListClassNameRef.current}></img>
								</Link>
							</div>
							<div className="about-us" tabIndex={0}
								onClick={indicateCurrentPage} onKeyPress={indicateCurrentPage}>
								<Link to="/knowledge-tree">
									<img src={networksAsset} alt={messageMap("navigation.networks", "image")}
										className={questsClassNameRef.current}></img>
								</Link>
							</div>
						</div>

						<div className="button-group-container">
							<button className="bell"
								onClick={showNotifications} onKeyPress={showNotifications}
								onMouseOver={showNotifications} onFocus={showNotifications}
								onMouseLeave={hideNotifications} onBlur={hideNotifications}>
								<img className="bell-icon" src={notificationAsset} alt={messageMap("account.settings", "image")}></img>
								<div className={props.clearNotifications === "1" ? `${notificationDot} hide inline-notification` : `${notificationDot} inline-notification`} ref={notificationRef}></div>
							</button>
							<div className={`notifications-container logged-in ${notificationDisplay}`}>
								{/* <Notification msgClickHandler={openModal} popUpCTAHandler={notificationCTA}
									clearNotificationsHandler={clearNotifications} /> */}
								{notificationContents}
							</div>
							<div className="neurons-counter-container">
								<img src={neuronImage} alt={neuronImageAltText} />
								<span className="neurons-counter">
									{paddedNeuronBalance}
								</span>
								<a className="neurons-plus" href="/quotes">+</a>
							</div>

							<Username hideUsername={true} customClass="navbar"
								mouseOverHandler={showAccountActions} mouseOutHandler={hideAccountActions} />

							<div className={`account-actions ${accountDisplay}`}>
								<ul>
									{/* TODO: to include in future */}
									{/* <li>
									My learning
								</li>
								<li>
									My cart
								</li>
								<li>
									Wishlist
								</li>
								<li>
									Messages
								</li>
								<li>
									Account settings
								</li> */}
									<li>
										<Link to="/profile" className="list-link">
											{messageMap("links.profileSettings", "button")}
										</Link>
									</li>
									<li>
										<Link to="/upload-video" className="list-link"
											onClick={checkIfLoggedInBeforeGoing} onKeyPress={checkIfLoggedInBeforeGoing}>
											{messageMap("links.upload", "button")}
										</Link>
									</li>
									<li>
										<Link to="/edit-videos" className="list-link">
											{messageMap("links.editVideos", "button")}
										</Link>
									</li>
									<li>
										<Link to="/quotes" className="list-link">
											{messageMap("links.exerShop", "button")}
										</Link>
									</li>
									{
										props.isInAGroup
											?
											<li>
												<Link to="/groups" className="list-link">
													{messageMap("links.groups", "button")}
												</Link>
											</li>
											:
											null
									}
									<li tabIndex={0} onClick={logOut} onKeyPress={logOut}>
										{messageMap("links.logOut", "button")}
									</li>
								</ul>
							</div>
						</div>
					</Fragment>
					:
					<Fragment>
						<div className="button-group-container">
							<div className="class-list" tabIndex={0}
								onClick={indicateCurrentPage} onKeyPress={indicateCurrentPage}>
								<Link to="/video-list">
									<img src={learnAsset} alt={messageMap("navigation.videoList", "image")}
										className={videoListClassNameRef.current}></img>
								</Link>
							</div>
							<div className="class-list" tabIndex={0}
								onClick={indicateCurrentPage} onKeyPress={indicateCurrentPage}>
								<Link to="/practice-category">
									<img src={practiceAsset} alt={messageMap("navigation.practice", "image")}
										className={practiceClassNameRef.current}></img>
								</Link>
							</div>
							<div className="class-list" tabIndex={0}
								onClick={indicateCurrentPage} onKeyPress={indicateCurrentPage}>
								<Link to="/topics-info-list">
									<img src={textbookAsset} alt={messageMap("navigation.topicsInfoList", "image")}
										className={topicsInfoListClassNameRef.current}></img>
								</Link>
							</div>
							{/* TODO: Only uncomment once we create associated features for creating, updating, deleting, and viewing classes */}
							{/* <div className="class-list">
							<Link to="/class-list">
								Classes
							</Link>
						</div> */}
							<div className="about-us" tabIndex={0}
								onClick={indicateCurrentPage} onKeyPress={indicateCurrentPage}>
								<Link to="/knowledge-tree">
									<img src={networksAsset} alt={messageMap("navigation.networks", "image")}
										className={questsClassNameRef.current}></img>
								</Link>
							</div>
						</div>

						<div className="button-group-container">
							<button className="bell"
								onClick={showNotifications} onKeyPress={showNotifications}
								onMouseOver={showNotifications} onFocus={showNotifications}
								onMouseLeave={hideNotifications} onBlur={hideNotifications}>
								<img className="bell-icon" src={notificationAsset} alt={messageMap("notifications.bell", "image")}></img>
								<div className={props.clearNotifications === "1" ? `${notificationDot} hide inline-notification` : `${notificationDot} inline-notification`} ref={notificationRef}></div>
							</button>
							<div className={`notifications-container ${notificationDisplay}`}>
								{/* <Notification msgClickHandler={openModal} popUpCTAHandler={notificationCTA}
									clearNotificationsHandler={clearNotifications} /> */}
								{notificationContents}
							</div>
							<button className="sign-up" onClick={() => openModal("signUp")} onKeyPress={() => openModal("signUp")}>
								{messageMap("links.signUp", "button")}
							</button>
							<button className="login" onClick={() => openModal("login")} onKeyPress={() => openModal("login")}>
								{messageMap("links.login", "button")}
							</button>
						</div>
					</Fragment>
			}
		</div>,
		<Fragment>
			<img className="hamburger" role="button" ref={hamburgerRef}
				src={hamburgerAsset} alt={messageMap("hamburger", "image")}
				onClick={openSideNav} onKeyPress={openSideNav} />
			<img className="x-icon hide" role="button" ref={xIconRef}
				src={xWhiteAsset} alt={messageMap("xIcon", "image")}
				onClick={xIconClick} onKeyPress={xIconClick} />
		</Fragment>
	);

	useEffect(() => {
		// TODO: replace this external API. It sometimes takes forever to respond, so it looks like the page takes a long time to load.
		// getIp();
		// earlyAccessCheck();

		const ownerId = localStorage.getItem("ownerId");
		if (ownerId) {
			setOwnerId(ownerId);
			autoLogin();
		}

		if (props.clearNotifications !== "1") {
			notificationRef.current.className = `${notificationDot} inline-notification`;
		}
		else if (props.clearNotifications === "1") {
			notificationRef.current.className = `${notificationDot} inline-notification hide`;
		}

		checkNotifications();
	}, [userSessionRef]);

	// For checking invalid routing when user isn't logged in
	useEffect(() => {
		const currentLocation = location.pathname;

		if (localStorage.getItem("ownerId") === null && ["/upload-video", "/edit-videos", "/profile"].includes(currentLocation)) {
			pushHistoryTo("/");
		}
	});

	useEffect(() => {
		generateStructuredDate();
		initializeExplicitDictionaryTree();
		indicateCurrentPage();
		generateAllKnowledgeNetworks();
	}, [initializedOnce]);

	async function getIp() {
		await fetch("https://api.ipify.org?format=json")
			.then(response => {
				return response.json();
			})
			.then(data => {
				checkForLockedIp(data.ip);
			});
	}

	navBarClass = "header-container-not-home";

	function generateStructuredDate() {
		// Reference: https://developers.google.com/search/docs/appearance/structured-data/logo
		setStructuredLogoJSON(
			JSON.stringify(
				{
					"@context": "https://schema.org",
					"@type": "Organization",
					"image": "https://www.exerinstitute.com/images/home/exer_logo_banner.svg",
					"url": "https://www.exerinstitute.com",
					"logo": "https://www.exerinstitute.com/images/home/exer_logo.svg",
					"name": "Exer Institute",
					"legalName": "Exer Institute",
					"description": "Revolutionizing online learning through engaging videos, auto-generated problems, interactive articles, and highly-trained teachers.",
					"email": "support@exerinstitute.com",
					"address": {
						"@type": "Postal Address",
						"streetAddress": "447 Sutter St Ste 405, #512, San Francisco",
						"addressLocality": "California",
						"addressRegion": "Ca",
						"postalCode": 94108,
						"addressCountry": "US"
					},
					"foundingDate": "2024-01"
				}
			)
		);
	}

	function checkNotifications() {
		const pathVariables = {
			ownerId: localStorage.getItem("ownerId")
		};
		listAllPromoCodesAPI(pathVariables, promoCodes => {
			if (promoCodes.length) {
				setNotificationDot("dot");
			}

			setNotificationContents(
				<Notification promoCodes={promoCodes} msgClickHandler={openModal}
					popUpCTAHandler={notificationCTA} clearNotificationsHandler={clearNotifications} />
			);
		});
	}

	function pushHistoryTo(url) {
		window.scrollTo(0, 0);
		history.push(url);
	}

	function checkForLockedIp(ip) {
		blockIpGetAPI({ ip: ip }, response => {
			userSessionRef.current["ip"] = ip;
			props.setIP(ip);
			if (response.ipAddress) {
				userSessionRef.current["ipCreatedAt"] = response.createdAt;
				userSessionRef.current["ipLocked"] = true;
				props.setIPCreatedAt(response.createdAt);
				props.setIPLocked(true);

				const storedTimedOutStartTime = Number(store.getState().account.timedOutStartTime);
				const currentTimestamp = Date.now();
				let newTimeoutDuration = LOG_IN_TIMEOUT;
				if (storedTimedOutStartTime + LOG_IN_TIMEOUT > currentTimestamp) {
					newTimeoutDuration = LOG_IN_TIMEOUT - (currentTimestamp - storedTimedOutStartTime);
				}

				if (checkLoginTimeout()) {
					props.setLogInTimeoutStartTime(currentTimestamp, newTimeoutDuration, () => {
						unblockLoginUI();
					});
				}
				else {
					unblockLoginUI();
				}
			}
			else {
				userSessionRef.current["ipLocked"] = false;
				props.setIPLocked(false);
			}
		});
	}

	function showNotifications() {
		setNotificationDisplay("show");
	}

	function hideNotifications() {
		setNotificationDisplay("");
	}

	function clearNotifications() {
		notificationRef.current.className = `${notificationDot} hide inline-notification`;
	}

	function earlyAccessCheck() {
		openModal("earlyAccess");
	}

	function autoLogin() {
		const payload = {
			ownerId: localStorage.getItem("ownerId"),
			jwt: localStorage.getItem("jwt")
		};

		if (payload.ownerId && payload.jwt) {
			autoLoginAPI(payload, response => {
				props.logInUser(response);
				setDomModal(null);

				// TODO: Only uncomment once we create associated features for creating, updating, deleting, and viewing classes
				// Will have to decide where to direct users: class or video list?
				// history.location.pathname === "/" && pushHistoryTo("/class-list");
				// hiding this, since we've moved video list page in home "/", but keeping this in case we change our minds, which is likely
				// history.location.pathname === "/" && pushHistoryTo("/video-list");
			});
		}
	}

	function openSideNav() {
		sideNavRef.current.className = "sideNav";
		hamburgerRef.current.className = "hamburger hide";
		xIconRef.current.className = "x-icon";
		sideNavContainerRef.current.className = "side-nav-container";
		notificationsContainerRef.current.className = "notifications-container hide";
	}

	function checkIfLoggedInBeforeGoing(event) {
		if (!props.ownerId) {
			event.preventDefault();
			openModal("signUp", messageMap("account.signUp.signUpFirst", "generic"));
		}
		else {
			resetSideNavToDefault();
		}
	}

	function toggleNotifications() {
		sideNavContainerRef.current.className = "side-nav-container hide";
		notificationsContainerRef.current.className = "notifications-container";
	}

	function xIconClick() {
		if (notificationsContainerRef.current.className === "notifications-container") {
			sideNavContainerRef.current.className = "side-nav-container";
			notificationsContainerRef.current.className = "notifications-container hide";
		}
		else if (sideNavContainerRef.current.className === "side-nav-container") {
			sideNavRef.current.className = "sideNav hide";
			hamburgerRef.current.className = "hamburger";
			xIconRef.current.className = "x-icon hide";
		}
	}

	function navigateToSubPage(domKey) {
		resetSideNavToDefault();
		props.setProfileSubNav(domKey);
	}

	function indicateCurrentPage() {
		const curUrl = window.location.href;

		// TODO: need to account for all direct links, and link navigation to make this really work
		// if (curUrl.includes("practice-category")) {
		// 	videoListClassNameRef.current = "";
		// 	practiceClassNameRef.current = "click";
		// 	questsClassNameRef.current = "";
		// }
		// else if (curUrl.includes("knowledge-tree")) {
		// 	videoListClassNameRef.current = "";
		// 	practiceClassNameRef.current = "";
		// 	questsClassNameRef.current = "click";
		// }
		// else if (curUrl.includes("video-list") || curUrl.slice(-1) === "/") {
		// 	videoListClassNameRef.current = "click";
		// 	practiceClassNameRef.current = "";
		// 	questsClassNameRef.current = "";
		// }
		// else {
		// 	videoListClassNameRef.current = "";
		// 	practiceClassNameRef.current = "";
		// 	questsClassNameRef.current = "";
		// }
	}

	function toggleAccountDropdown() {
		if (usernameDropdownRef.current.classList.contains("hide")) {
			usernameDropdownRef.current.className = "dropdown-account-actions";
			usernameChevronRef.current.className = "chevron-down";
		}
		else {
			usernameDropdownRef.current.className = "dropdown-account-actions hide";
			usernameChevronRef.current.className = "chevron-up";
		}
	}

	function toggleProfileDropdown() {
		if (profileDropdownRef.current.classList.contains("hide")) {
			profileDropdownRef.current.className = "dropdown-profile-actions";
			profileChevronRef.current.className = "chevron-down";
		}
		else {
			profileDropdownRef.current.className = "dropdown-profile-actions hide";
			profileChevronRef.current.className = "chevron-up";
		}
	}

	function showAccountActions() {
		setAccountDisplay("show");
	}

	function hideAccountActions() {
		setAccountDisplay("");
	}

	function logOut() {
		const payload = {
			ownerId: props.ownerId,
			jwt: props.jwt
		};

		resetSideNavToDefault();
		logOutAPI(payload, () => {
			props.logOutUser();
			props.clearApiKeys();
			// TODO: Need to move this somewhere else
			// earlyAccessCheck();
		});
		pushHistoryTo("/");
	}

	function openAnotherModal(type, modalOverlayCount) {
		if (type === "terms" && modalOverlayCount === 1) {
			setDomModal1(
				<TermsNConditionsModal closeHandler={(e) => closeModal(e, 1)} />
			);
		}
		else if (type === "privacy" && modalOverlayCount === 1) {
			setDomModal1(
				<PrivacyPolicyModal closeHandler={(e) => closeModal(e, 1)} />
			);
		}
		else if (type === "signInPrivacy" && modalOverlayCount === 1) {
			setDomModal1(
				<Modal closeHandler={(e) => closeModal(e, 1)} closeType={"xButton"}
					closeButtonStyle={modalLawSchema.closeButtonStyle} modalContainerStyle={modalLawSchema.modalContainerStyle}>
					<div>
						{messageMap("account.signUp.privacy.data", "generic")}
					</div>
				</Modal>
			);
		}
		else if (type === "login") {
			if (modalOverlayCount === 0) {
				// setDomModal(null);
				// openModal("login");
				const modalSchemaObj = modalSchema(closeModal, openAnotherModal, checkUsername);
				openLoginModal(null, modalSchemaObj, modalOverlayCount);
			}
			else if (modalOverlayCount === 1 || modalOverlayCount === 2) {
				const modalSchemaObj = modalSchema(closeModal, openAnotherModal, checkUsername);
				openLoginModal(null, modalSchemaObj, modalOverlayCount);
			}
		}
		else if (type === "signUp" && modalOverlayCount === 1) {
			const miscArgs = {
				signUp: {
					loginModalOverlayCount: 2,
					accessCode: true
				}
			};
			const modalSchemaObj = modalSchema(closeModal, openAnotherModal, checkUsername, miscArgs);
			openSignUpModal(null, modalSchemaObj, modalOverlayCount);
		}
	}

	function openModal(type, customTitle, ...args) {
		const modalSchemaObj = modalSchema(closeModal, openAnotherModal, checkUsername);

		if (type.includes("signUp")) {
			openSignUpModal(customTitle, modalSchemaObj, 0);
		}
		else if (type.includes("login")) {
			openLoginModal(customTitle, modalSchemaObj, 0);
		}
		else if (type.includes("earlyAccess")) {
			const earlyAccessModalSchema = modalSchemaObj["earlyAccess"];
			const title = messageMap("account.access.earlyAccess.header", "generic");

			if (!userSessionRef.current["ipLocked"]) {
				setDomModal(
					<Modal title={title} subHeader={earlyAccessModalSchema.subHeader}
						stateProps={earlyAccessModalSchema.stateProps} inputs={earlyAccessModalSchema.inputs}
						submitHandler={getEarlyAccess} />
				);
			}
			else {
				setDomModal(
					<Modal title={title} subHeader={earlyAccessModalSchema.subHeader}
						stateProps={earlyAccessModalSchema.stateProps} inputs={earlyAccessModalSchema.inputs}
						disableForms={true}
						disabledMsg={messageMap("account.access.attempts", "generic")} />
				);
			}
		}
		else if (type.includes("notification")) {
			const titleStyle = {
				fontFamily: "Montserrat-SemiBold",
				fontSize: "40px",
				marginBottom: "40px"
			},
				subHeaderStyle = {
					fontSize: "24px",
					fontFamily: "Montserrat-SemiBold",
					margin: "0px",
					marginBottom: "24px",
					width: "initial"
				},
				footerStyle = {
					fontSize: "16px",
					textAlign: "justify",
					margin: "0 0 40px",
					width: "inherit"
				},
				bodyStyle = {
					fontSize: "18px",
					textAlign: "justify",
					marginBottom: "40px"
				},
				promoItemStyle = {
					marginBottom: "24px"
				},
				submitHandlerStyle = {
					margin: "0px",
					backgroundColor: "#00d182",
					borderRadius: "60px"
				},
				modalContainerStyle = {
					fontFamily: "Montserrat-Regular",
					borderRadius: "16px",
					backgroundColor: "#fff",
					padding: "32px",
					maxWidth: "990px",
					overflow: "auto",
					height: window.innerWidth < 768 ? "100vh" : "inherit"
				};

			setDomModal(
				<Modal title={customTitle}
					modalContainerStyle={modalContainerStyle} titleStyle={titleStyle}
					subHeader={args[0].subheader} subHeaderStyle={subHeaderStyle}
					footer={args[0].footer} footerStyle={footerStyle}
					closeHandler={(e) => closeModal(e)} closeType={"xButton"}
					submitHandler={e => args[0]["ctaHandler"](args[0].ctaHandlerArgs)}
					submitHandlerStyle={submitHandlerStyle}
					submitText={messageMap("links.signUp", "button")}>
					<div style={bodyStyle}>
						<div style={promoItemStyle}>
							{args[0].body[0]}
						</div>
						<div style={promoItemStyle}>
							{args[0].body[1]}
						</div>
						<div>
							{args[0].body[2]}
						</div>
					</div>
				</Modal>
			);
		}
	}

	function notificationCTA(cta) {
		if (cta === "signUp") {
			openAnotherModal("signUp", 1);
		}
	}

	function openSignUpModal(customTitle, modalSchemaObj, modalOverlayCount) {
		const signUpModalSchema = modalSchemaObj["signUp"];
		const inputsToValidate = signUpModalSchema.stateProps.slice(0, signUpModalSchema.stateProps.length - 2),
			errorMsg = (
				<div className="error-msg">
					{messageMap("account.signUp.errorMsg.msg", "generic")}
					<span>
						{messageMap("account.signUp.errorMsg.highlight", "generic")}
					</span>
				</div>
			);
		const title = customTitle != null ? customTitle : messageMap("account.signUp.header", "generic");
		const titleStyle = customTitle != null ? { "width": "350px" } : {};
		const overlayMap = {
			"0": setDomModal,
			"1": setDomModal1
		};

		overlayMap[modalOverlayCount](
			// TODO: include in Modal once we start supporting school credits
			// subHeader={signUpModalSchema.subHeader}
			<Modal stateProps={signUpModalSchema.stateProps} closeHandler={e => closeModal(e, modalOverlayCount)} submitHandler={signUp}
				title={title} titleStyle={titleStyle} subHeader={signUpModalSchema.subHeader}
				inputs={signUpModalSchema.inputs} footer={signUpModalSchema.disclaimer} errorMsg={errorMsg}
				inputsToValidate={inputsToValidate} />
		);
	}

	function openLoginModal(customTitle, modalSchemaObj, modalOverlayCount) {
		const loginModalSchema = modalSchemaObj["login"];
		const title = customTitle != null ? customTitle : messageMap("account.login.header", "generic");

		checkLoginTimeout();
		if (!userSessionRef.current["ipLocked"]) {
			const overlayMap = {
				"0": setDomModal,
				"1": setDomModal1,
				"2": setDomModal2
			};

			overlayMap[modalOverlayCount](
				<Modal title={title} stateProps={loginModalSchema.stateProps} inputs={loginModalSchema.inputs}
					subHeader={loginModalSchema.subHeader}
					closeHandler={e => closeModal(e, modalOverlayCount)} submitHandler={logIn} />
			);
		}
		else {
			setDomModal(
				<Modal title={title} stateProps={loginModalSchema.stateProps} inputs={loginModalSchema.inputs}
					subHeader={loginModalSchema.subHeader}
					closeHandler={e => closeModal(e, 0)} disableForms={true}
					disabledMsg={messageMap("account.login.attempts", "generic")} />
			);
		}
	}

	function checkUsername(...args) {
		const inputObject = args[1];
		if (inputObject.isValid) {
			const target = args[0].target;
			usernameAvailabilityAPI({ username: inputObject.value }, (resp) => displayUsernameResultIcon(resp, target));
		}
	}

	function displayUsernameResultIcon(resp, event) {
		// remove existing checks or exes icons
		event.parentNode.childNodes.forEach(dom => {
			if (dom.tagName === "IMG" && dom.className === "") {
				dom.remove();
			}
		});

		if (!resp) {
			// Apologies. I know this is an anti-pattern, but I don't know how to do this in the 'React' way
			let img = document.createElement("img");
			img.src = checkAsset;
			img.alt = messageMap("checkUsername.check", "image");
			img.style.display = "inline-block";
			img.style.marginLeft = "5px";
			img.style.height = "25px";
			event.parentNode.insertBefore(img, event.nextSibling);
		}
		else {
			// Apologies. I know this is an anti-pattern, but I don't know how to do this in the 'React' way
			let img = document.createElement("img");
			img.src = exAsset;
			img.alt = messageMap("checkUsername.cross", "image");
			img.style.display = "inline-block";
			img.style.marginLeft = "5px";
			img.style.height = "25px";
			event.parentNode.insertBefore(img, event.nextSibling);
		}
	}

	function closeModal(e, type) {
		if (["modal-block", "cancel", "fullRegistration", "icon", "close-button"].includes(e.target.className)) {
			if (type === 1) {
				setDomModal1(null);
			}
			else if (type === 2) {
				setDomModal2(null);
			}
			else {
				setDomModal(null);
			}
		}
	}

	function closeAlert() {
		setAlert(null);
	}

	function resetSideNavToDefault() {
		sideNavRef.current.className = "sideNav hide";
		sideNavContainerRef.current.className = "side-nav-container";
		notificationsContainerRef.current.className = "notifications-container hide";

		if (hamburgerRef != null && hamburgerRef.current != null && xIconRef != null && xIconRef.current != null) {
			hamburgerRef.current.className = "hamburger";
			xIconRef.current.className = "x-icon hide";
		}
	}

	function displaySignUpMessage(data, email) {
		setSpinner(null);

		if (data.httpStatus) {
			setAlert(
				<Alert closeHandler={closeAlert} type="information" msg={messageMap(data.message, "api")} />
			);
		}
		else {
			setDomModal(null);
			setDomModal1(null);
			setDomModal2(null);
			let message = messageMap("accounts.created.success", "api");
			message = message.replace("${email}", email);

			setAlert(
				<Alert closeHandler={closeAlert} type="success" msg={message} />
			);
		}
	}

	function displayErrorMessage(msg) {
		setSpinner(null);
		setAlert(
			<Alert closeHandler={closeAlert} type="error" msg={messageMap(msg, "api")} />
		);
	}

	function signUp(signUpStates) {
		const usernameValue = signUpStates.signUpUser.value;

		if (checkHasExplicitWords(usernameValue)) {
			setAlert(
				<Alert type="error" closeHandler={closeAlert} msg={messageMap("input.explicit.input.username", "validation")}></Alert>
			);
			return;
		}

		const payload = {
			email: signUpStates.signUpEmail.value,
			hashedPassword: signUpStates.signUpPass.value,
			username: usernameValue,
			accessKey: signUpStates.signUpAccessKey && signUpStates.signUpAccessKey.value ? signUpStates.signUpAccessKey.value : ""
		};

		const spinnerContainerStyle = {
			"marginLeft": "-50px",
			"marginTop": "-120px"
		};
		setSpinner(<Spinner spinnerContainerStyle={spinnerContainerStyle} />);
		sideNavRef.current.className = "sideNav hide";
		signUpAPI(payload,
			(data) => {
				if (data.message && data.message.includes("failed")) {
					displayErrorMessage(data.message);
				}
				else {
					props.logInUser(data);
					displaySignUpMessage(data, payload.email)
				}
			},
			(msg) => {
				displayErrorMessage(msg);
			}
		);
	}

	function logIn(loginStates, statusHandler, inputDisabler) {
		const payload = {
			username: loginStates.loginUser.value,
			password: loginStates.loginPass.value
		};

		if (payload.username != null && payload.password != null) {
			loginAPI(payload, (response) => {
				if (!response) {
					let msg;

					if (loginAttemptCount >= 3) {
						msg = (
							<div className="error-msg w275">
								{messageMap("account.login.attempts", "generic")}
							</div>
						);

						inputDisabler();
						userSessionRef.current["ipLocked"] = true;
						props.setIPLocked(true);
						blockIpPostAPI({ ip: userSessionRef.current.ip });
						props.setLogInTimeoutStartTime(Date.now(), LOG_IN_TIMEOUT, () => {
							unblockLoginUI();
							statusHandler("");
							inputDisabler(false);
						});
					}
					else {
						msg = (
							<div className="error-msg">
								{messageMap("account.usernamePassword", "validation")}
							</div>
						);

						++loginAttemptCount;
					}

					statusHandler(msg);
				}
				else {
					props.logInUser(response);
					setDomModal(null);
					setDomModal1(null);
					setDomModal2(null);
					// TODO: Only uncomment once we create associated features for creating, updating, deleting, and viewing classes
					// Will have to decide where to direct users: class or video list?
					// history.location.pathname === "/" && pushHistoryTo("/class-list");
					history.location.pathname === "/" && pushHistoryTo("/video-list");
					resetSideNavToDefault();
				}
			});
		}
	}

	function unblockLoginUI() {
		props.clearLogInTimeout();
		props.clearLoginAttemptCount();
		props.setIPLocked(false);
		loginAttemptCount = 0;
		unblockIpAPI({ ip: userSessionRef.current.ip });
	}

	function checkLoginTimeout() {
		const storedTimedOutStartTime = Number(store.getState().account.timedOutStartTime);
		const currentTimestamp = Date.now();
		let newTimeoutDuration = LOG_IN_TIMEOUT;

		if (storedTimedOutStartTime + LOG_IN_TIMEOUT > currentTimestamp) {
			newTimeoutDuration = LOG_IN_TIMEOUT - (currentTimestamp - storedTimedOutStartTime);
		}
		else {
			newTimeoutDuration = 0;
			userSessionRef.current["ipLocked"] = false;
		}

		return newTimeoutDuration;
	}

	function getEarlyAccess(accessStates, statusHandler, inputDisabler) {
		const payload = {
			accessKey: accessStates.accessKey.value,
			ownerId: -1
		};

		getReleaseAPI(payload, response => {
			if (response === "accounts.access.earlyRelease.notFound") {
				setAlert(
					<Alert closeHandler={closeAlert} type="information" msg={messageMap(response, "api")} />
				);
			}
			else if (response === "accounts.access.earlyRelease.granted") {
				setAlert(
					<Alert closeHandler={closeAlert} type="success" msg={messageMap(response, "api")} />
				);
				setDomModal(null);
			}
		});
	}

	return (
		<nav className={navBarClass}>
			<Helmet>
				<script type="application/ld+json">{structuredLogoJSON}</script>
			</Helmet>

			<div className="logo">
				<Link to="/">
					<img src={exerLogoBannerAsset} alt={messageMap("companyLogo", "image")}></img>
				</Link>
			</div>

			{/* <div className="search-container">
				<input className="input-box" aria-describedby="Search input for classes" type="search" placeholder="Search for classes here..."></input>
				<img className="search-btn" src={searchAsset} alt={messageMap("search", "image")}></img>
			</div> */}

			<div className="list-container">
				{topNav}
			</div>

			<div className="sideNav hide" ref={sideNavRef}>
				<div className="side-nav-container" ref={sideNavContainerRef}>

					{
						props.ownerId ?
						<Fragment>
							<button onClick={toggleAccountDropdown} onKeyPress={toggleAccountDropdown}>
								<img className="icon" src={accountAsset}
									alt={messageMap("account.profileView", "image")}
								></img>
								{props.username}
								<div className="chevron-up" ref={usernameChevronRef}></div>
							</button>

							<div className="dropdown-account-actions hide" ref={usernameDropdownRef}>
								<button className="sub-level1"
									onClick={toggleProfileDropdown} onKeyPress={toggleProfileDropdown}>
									<img className="icon" src={profileSettingsAsset}
										alt={messageMap("account.settings", "image")} />
									{messageMap("links.profileSettings", "button")}
									<div className="chevron-up" ref={profileChevronRef}></div>
								</button>
								<div className="dropdown-profile-actions hide" ref={profileDropdownRef}>
									<Link to="/profile?show=account" className="sub-level2 url-link"
										onClick={e => navigateToSubPage(ACCOUNT)} onKeyPress={e => navigateToSubPage(ACCOUNT)}>
										<img className="icon" src={accountSubOptionAsset}
											alt={messageMap("subPages.profile.account", "image")} />
										{messageMap("profile.account", "subPageNavigation")}
									</Link>

									<Link to="/profile?show=groups" className="sub-level2 url-link"
										onClick={e => navigateToSubPage(GROUPS)} onKeyPress={e => navigateToSubPage(GROUPS)}>
										<img className="icon" src={groupsSubOptionAsset}
											alt={messageMap("subPages.profile.groups", "image")} />
										{messageMap("profile.groups", "subPageNavigation")}
									</Link>

									<Link to="/profile?show=financials" className="sub-level2 url-link"
										onClick={e => navigateToSubPage(FINANCIALS)} onKeyPress={e => navigateToSubPage(FINANCIALS)}>
										<img className="icon" src={financialsSubOptionAsset}
											alt={messageMap("subPages.profile.financials", "image")} />
										{messageMap("profile.financials", "subPageNavigation")}
									</Link>
								</div>

								<button className="sub-level1" onClick={toggleNotifications} onKeyDown={toggleNotifications}>
									<img className="icon bell" src={notificationAsset}
										alt={messageMap("account.settings", "image")} />
									<div className={props.clearNotifications === "1" ? `${notificationDot} hide inline-notification` : `${notificationDot} logged-in inline-notification`} ref={notificationRef}></div>
									{messageMap("notification.title", "notification")}
								</button>

								<Link to="/upload-video" className="url-link sub-level1"
									onClick={checkIfLoggedInBeforeGoing} onKeyPress={checkIfLoggedInBeforeGoing}>
									<img className="icon" src={uploadAsset} alt={messageMap("learn.upload", "image")} />
									{messageMap("links.upload", "button")}
								</Link>

								<Link to="/edit-videos" className="url-link sub-level1"
									onClick={resetSideNavToDefault} onKeyPress={resetSideNavToDefault}>
									<img className="icon" src={editVideosAsset} alt={messageMap("learn.editVideos", "image")} />
									{messageMap("links.editVideos", "button")}
								</Link>

								<Link to="/quotes" className="url-link sub-level1"
									onClick={resetSideNavToDefault} onKeyPress={resetSideNavToDefault}>
									<img className="icon" src={shinyNeuronsAsset} alt={getAlt(0)} />
									{messageMap("links.exerShop", "button")}
								</Link>

								{
									props.isInAGroup
										?
										<Link to="/groups" className="url-link sub-level1">
											<img className="icon" src={groupsAsset} alt={messageMap("groups.page", "image")} />
											{messageMap("links.groups", "button")}
										</Link>
										:
										null
								}

								<button className="sub-level1" onClick={logOut} onKeyPress={logOut}>
									<img className="icon" src={logoutAsset} alt={messageMap("account.logout", "image")} />
									{messageMap("links.logOut", "button")}
								</button>
							</div>
						</Fragment>
						:
						<Fragment>
							<button onClick={() => openModal("signUp")} onKeyPress={() => openModal("signUp")}>
								{messageMap("links.signUp", "button")}
							</button>

							<button onClick={() => openModal("login")} onKeyPress={() => openModal("login")}>
								{messageMap("links.login", "button")}
							</button>

							<div className="navigation-spacer"></div>
							<button onClick={toggleNotifications} onKeyDown={toggleNotifications}>
								<img className="side-nav-icon bell" src={notificationAsset} alt={messageMap("notifications.bell", "image")}></img>
								<div className={props.clearNotifications === "1" ? `${notificationDot} hide inline-notification` : `${notificationDot} inline-notification`} ref={notificationRef}></div>
								<div className="nav-text">
									{messageMap("notification.title", "notification")}
								</div>
							</button>

							<Link className="url-link" to="/upload-video"
								onClick={checkIfLoggedInBeforeGoing} onKeyPress={checkIfLoggedInBeforeGoing}>
								<img className="side-nav-icon" src={uploadAsset} alt={messageMap("learn.upload", "image")}></img>
								<div className="nav-text">
									{messageMap("links.upload", "button")}
								</div>
							</Link>
						</Fragment>
					}

					<div className="navigation-spacer"></div>
					<Link className="url-link" to="/video-list"
						onClick={resetSideNavToDefault} onKeyPress={resetSideNavToDefault}>
						<img className="side-nav-icon" src={learnBlackAsset} alt={messageMap("learn.videoList", "image")}></img>
						<div className="nav-text">
							{messageMap("links.learn", "button")}
						</div>
					</Link>

					<Link className="url-link" to="/practice-category"
						onClick={resetSideNavToDefault} onKeyPress={resetSideNavToDefault}>
						<img className="side-nav-icon" src={practiceBlackAsset} alt={messageMap("learn.networks", "image")}></img>
						<div className="nav-text">
							{messageMap("links.practice", "button")}
						</div>
					</Link>

					<Link className="url-link" to="/topics-info-list"
						onClick={resetSideNavToDefault} onKeyPress={resetSideNavToDefault}>
						<img className="side-nav-icon" src={textbookBlackAsset} alt={messageMap("learn.topicsInfo", "image")}></img>
						<div className="nav-text">
							{messageMap("links.topicsInfoList", "button")}
						</div>
					</Link>

					<Link className="url-link" to="/knowledge-tree"
						onClick={resetSideNavToDefault} onKeyPress={resetSideNavToDefault}>
						<img className="side-nav-icon" src={networksBlackAsset} alt={messageMap("learn.networks", "image")}></img>
						<div className="nav-text">
							{messageMap("links.knowledgeTree", "button")}
						</div>
					</Link>

					<div className="navigation-spacer"></div>

					<Link className="url-link" to="/about-us"
						onClick={resetSideNavToDefault} onKeyPress={resetSideNavToDefault}>
						<img className="side-nav-icon" src={aboutUsAsset} alt={messageMap("learn.networks", "image")}></img>
						<div className="nav-text">
							{messageMap("links.aboutUs", "button")}
						</div>
					</Link>
				</div>

				<div className="notifications-container hide" ref={notificationsContainerRef}>
					{/* <Notification msgClickHandler={openModal} popUpCTAHandler={notificationCTA}
						clearNotificationsHandler={clearNotifications} /> */}
					{notificationContents}
				</div>
			</div>

			{spinner}

			{domModal}

			{domModal1}

			{domModal2}

			{alert}

		</nav>
	);
}

NavBar.defaultProps = {
	neurons: 0
};

NavBar.propTypes = {
	// Redux props
	clearNotifications: PropTypes.string,
	ownerId: PropTypes.string,
	jwt: PropTypes.string,
	neurons: PropTypes.number,
	isInAGroup: PropTypes.bool,
	username: PropTypes.string,
	timedOutStartTime: PropTypes.number,
	logInUser: PropTypes.func.isRequired,
	logOutUser: PropTypes.func.isRequired,
	setLogInTimeoutStartTime: PropTypes.func.isRequired,
	clearLogInTimeout: PropTypes.func.isRequired,
	clearLoginAttemptCount: PropTypes.func.isRequired,
	setIP: PropTypes.func.isRequired,
	setIPLocked: PropTypes.func.isRequired,
	setIPCreatedAt: PropTypes.func.isRequired,
	setProfileSubNav: PropTypes.func.isRequired
};

export default connect(
	navigationSelector,
	{ 
		logInUser, logOutUser, clearApiKeys,
		setLogInTimeoutStartTime, clearLogInTimeout, clearLoginAttemptCount,
		setIP, setIPLocked, setIPCreatedAt,
		setProfileSubNav
	}
)(NavBar);